import { Button, CircularProgress, FormControlLabel, MenuItem, Select, Switch, Typography } from "@mui/material";
import { DatePicker, TimeIcon } from "@mui/x-date-pickers";
import React, { Fragment, useState } from "react";
import moment from "moment";
import { Save } from "@mui/icons-material";
import { SaveReturnRequestHandler } from "../cqrs/save-return-request/save-return-request-handler";
import { notificationModel } from "../../../../common/component/notifications/notification-model";
import { SelectAsync } from "../../../../common/component/select-async/select-async";
import { GetBtmCarriersHandler } from "../cqrs/get-btm-carriers/get-btm-carriers-handler";
import { GetBtmCarriersQuery } from "../cqrs/get-btm-carriers/get-btm-carriers-query";
import { useEffectAsync } from "../../../../common/hooks/use-effect-async";
import { BtmCarrier } from "../model/btm-carrier";
import { BtmShippingInfo } from "../model/btm-shipping-info";
import { BtmRequestTrackingManagement } from "./btm-request-tracking-management";
import { BtmReturnRequestServiceWindow } from "../model/btm-return-request-service-window";
import { BtmReturnRequest } from "../model/btm-return-request";
import { t } from "i18next";

interface IBtmRequestLogisticSetupProps {
    readOnly: boolean;
    request: BtmReturnRequest;
    onRequestUpdated?: (request: BtmReturnRequest) => void;
}

export const BtmRequestLogisticSetup = (props: IBtmRequestLogisticSetupProps) => {
    const [carriersList, setCarriersList] = useState<BtmCarrier[] | undefined>();
    const [isCarriersListLoading, setIsCarriersListLoading] = useState(true);
    const [isCustomCarrierSelected, setIsCustomCarrierSelected] = useState(false);

    const [carrier, setCarrier] = useState<BtmCarrier | undefined>(props.request.carrier ?
        new BtmCarrier(props.request.carrier, props.request.isBulkyCarrier ?? false) : undefined);
    const [pickupDate, setPickupDate] = useState<moment.Moment | undefined>(props.request.pickupDate);
    const [serviceWindow, setServiceWindow] = useState<string>(props.request.serviceWindow?.toString() || "Unknown");
    const [isAppointmentRequested, setIsAppointmentRequested] = useState<boolean>(props.request.isAppointmentRequested);

    const [shippingDetails, setShippingDetails] = useState<BtmShippingInfo[]>(props.request.shippingInfo.length > 0 ?
        props.request.shippingInfo : BtmShippingInfo.createDefault());

    const [isSaving, setIsSaving] = useState(false);
    const [hasFormChanged, setHasFormChanged] = useState<boolean | undefined>(undefined);

    useEffectAsync(async () => {
        setCarriersList(await GetBtmCarriersHandler(new GetBtmCarriersQuery("")));
        setIsCarriersListLoading(false);
    }, []);

    const handleCarrierChanged = (value: BtmCarrier | undefined) => {
        setCarrier(prev => {
            if (prev?.name === value?.name && prev?.isBulky === value?.isBulky) {
                return prev;
            }

            setHasFormChanged(true);
            return value;
        });
    };

    const handlePickupDateChanged = (value: moment.Moment | undefined) => {
        setHasFormChanged(true);
        setPickupDate(value);
    };

    const handleServiceWindowChanged = (value: string) => {
        setHasFormChanged(true);
        setServiceWindow(value);
    };

    const handleIsAppointmentRequested = (value: boolean) => {
        setHasFormChanged(true);
        setIsAppointmentRequested(value);
    };

    const handleShippingInfoChanged = (index: number, shippingInfo: BtmShippingInfo) => {
        setHasFormChanged(true);
        setShippingDetails(prev =>
            prev.map((item, idx) => idx === index ? shippingInfo : item));
    };

    const handleAddTracking = () => {
        setShippingDetails(prev => [...prev, new BtmShippingInfo({})]);
    };

    const handleSaveRequest = async () => {
        if (!props.onRequestUpdated) {
            return;
        }

        try {
            setIsSaving(true);
            props.onRequestUpdated(await SaveReturnRequestHandler(
                props.request.setCarrier(carrier)
                    .setAppointment(pickupDate, serviceWindow, isAppointmentRequested)
                    .setShippingInfo(shippingDetails)));
            notificationModel.addSuccessMessage(t("components.blueBrandReturns.page.details.form.successMessage"));
            setHasFormChanged(false);
        } catch (err) {
            notificationModel.addErrorMessage(t("components.blueBrandReturns.page.details.form.errorMessage"));
        } finally {
            setIsSaving(false);
        }
    };

    const handleCustomCarrierToggle = (isCustom: boolean) => {
        setIsCustomCarrierSelected(isCustom);
    };

    return <div className={"btm-request-details__wrapper"}>
        <div className={"btm-request-form"}>
            <div className={"btm-request-form__row--multi"}>
                <div>
                    <Typography variant={"h5"} className={"btm-request-form__label"} color={"textSecondary"}>
                        {t("components.blueBrandReturns.page.details.form.carrier.label")}
                    </Typography>
                    <SelectAsync withCustomValue readOnly={props.readOnly}
                                 isLoading={isCarriersListLoading}
                                 options={carriersList ?? []}
                                 getOptionLabel={(o: BtmCarrier) => o.name}
                                 getOptionValue={(o: BtmCarrier) => o.name}
                                 isEqual={(o1: BtmCarrier, o2: BtmCarrier) =>
                                     o1.name === o2.name && o1.isBulky === o2.isBulky}
                                 value={carrier}
                                 onValueChange={handleCarrierChanged}
                                 customValueOptionLabel={t("components.blueBrandReturns.page.details.form.carrier.customValue")}
                                 buildCustomValue={text => new BtmCarrier(text, carrier?.isBulky)}
                                 onCustomValueSelected={handleCustomCarrierToggle} />
                </div>
                {isCustomCarrierSelected && <div>
                    <Typography variant={"h5"} className={"btm-request-form__label"} color={"textSecondary"}>
                        {t("components.blueBrandReturns.page.details.form.isBulky.label")}
                    </Typography>
                    <Switch checked={carrier?.isBulky ?? false} color={"primary"}
                            disabled={props.readOnly}
                            onChange={e =>
                                handleCarrierChanged(new BtmCarrier(carrier?.name ?? "", e.target.checked))} />
                </div>}
            </div>
            <div className={"btm-request-form__row--multi"}>
                <div>
                    <Typography variant={"h5"} className={"btm-request-form__label"} color={"textSecondary"}>
                        {t("components.blueBrandReturns.page.details.form.pickupDate.label")}
                    </Typography>
                    <DatePicker readOnly={props.readOnly} minDate={moment()} value={pickupDate} onChange={handlePickupDateChanged} />
                </div>
                <div>
                    <Typography variant={"h5"} className={"btm-request-form__label"} color={"textSecondary"}>
                        {t("components.blueBrandReturns.page.details.form.serviceWindow.label")}
                    </Typography>
                    <Select value={serviceWindow}
                            readOnly={props.readOnly}
                            disabled={pickupDate === undefined}
                            IconComponent={TimeIcon}
                            onChange={e => handleServiceWindowChanged(e.target.value)}
                            sx={{ "& .MuiSelect-iconOpen": { transform: "none" }}}>
                        {[BtmReturnRequestServiceWindow.Unknown, BtmReturnRequestServiceWindow.Morning,
                            BtmReturnRequestServiceWindow.Noon, BtmReturnRequestServiceWindow.Afternoon,
                            BtmReturnRequestServiceWindow.Evening].map(item =>
                            <MenuItem value={item.toString()} key={`option-${item}`}>
                                {t(`components.blueBrandReturns.serviceWindow.${item}`)}
                            </MenuItem>)}
                    </Select>
                </div>
            </div>
            <div className={"btm-request-form__row--with-border"}>
                <FormControlLabel control={<Switch checked={isAppointmentRequested} color={"primary"}
                                                   onChange={e => handleIsAppointmentRequested(e.target.checked)}
                                                   disabled={props.readOnly} />}
                                  label={<Fragment>
                                      <Typography variant={"h5"} color={"textPrimary"} className={"btm-request-form__label"}>
                                          {t("components.blueBrandReturns.page.details.form.requestAppointment.label")}
                                      </Typography>
                                      <Typography variant={"h5"} color={"textSecondary"} className={"btm-request-form__label"}>
                                          {t("components.blueBrandReturns.page.details.form.requestAppointment.description")}
                                      </Typography>
                                  </Fragment>}
                />
            </div>
            <BtmRequestTrackingManagement isDisabled={isAppointmentRequested || props.readOnly}
                                          shippingInfo={shippingDetails}
                                          onTrackingAdded={handleAddTracking}
                                          onShippingInfoChange={handleShippingInfoChanged} />
            <div className={"btm-request-form__row"}>
                <Button onClick={handleSaveRequest} disabled={isSaving || !hasFormChanged || props.readOnly}
                        startIcon={isSaving ? <CircularProgress color={"inherit"} size={"14px"} /> : <Save />}
                        variant={"contained"} color={"secondary"} size={"large"}>
                    {t("components.blueBrandReturns.page.details.form.submit")}
                </Button>
            </div>
        </div>
    </div>;
};
